/**
 * Emit-time node ordering for KHR_interactivity export.
 *
 * The ratified spec (Node Object Validation) requires every `values` source
 * to be an EARLIER node and every `flows` target a LATER node, and states
 * that behavior graphs have no directed cycles. So the exported `nodes[]`
 * must be a topological order of the combined value + flow edge set, with
 * every edge pointing forward.
 *
 * Authoring order is the tiebreak (Kahn's algorithm, always taking the
 * lowest authored index that is ready), so a graph that is already in a
 * valid order is emitted unchanged and small edits produce small diffs.
 *
 * A cycle cannot be ordered. It is reported with the nodes on it, never
 * resolved by dropping an edge. Repetition is expressed acyclically by
 * re-entering through a custom event (the Interval macro, #896).
 */

import type { GraphData } from '../../scenedata/behaviors/behaviorgraphdata';

export type NodeOrderResult =
    | { ok: true; order: number[] }
    /** Each cycle is a closed walk of authored node indices, first node repeated last. */
    | { ok: false; cycles: number[][] };

/** Forward edges `from → to` implied by a graph's value and flow connections. */
export function graphEdges(graph: Pick<GraphData, 'nodes'>): Array<[from: number, to: number]> {
    const edges: Array<[number, number]> = [];
    const count = graph.nodes.length;
    graph.nodes.forEach((node, index) => {
        for (const value of Object.values(node.values ?? {})) {
            // Value source must come before its consumer.
            if ('node' in value && inRange(value.node, count)) edges.push([value.node, index]);
        }
        for (const flow of Object.values(node.flows ?? {})) {
            // Flow source must come before its target.
            if (inRange(flow.node, count)) edges.push([index, flow.node]);
        }
    });
    return edges;
}

/**
 * Topologically order a graph's nodes (value sources and flow sources first),
 * stable on authoring order. Out-of-range references are ignored here — the
 * conformance check reports them.
 */
export function orderGraphNodes(graph: Pick<GraphData, 'nodes'>): NodeOrderResult {
    const count = graph.nodes.length;
    const successors: number[][] = Array.from({ length: count }, () => []);
    const inDegree = new Array<number>(count).fill(0);
    for (const [from, to] of graphEdges(graph)) {
        successors[from].push(to);
        inDegree[to]++;
    }

    // `ready` is kept sorted ascending so the lowest authored index wins.
    const ready: number[] = [];
    for (let i = 0; i < count; i++) if (inDegree[i] === 0) ready.push(i);
    const order: number[] = [];
    while (ready.length > 0) {
        const next = ready.shift()!;
        order.push(next);
        for (const succ of successors[next]) {
            if (--inDegree[succ] === 0) insertSorted(ready, succ);
        }
    }

    if (order.length === count) return { ok: true, order };
    const remaining = new Set<number>();
    for (let i = 0; i < count; i++) if (inDegree[i] > 0) remaining.add(i);
    return { ok: false, cycles: findCycles(successors, remaining) };
}

/**
 * The graph's directed cycles over value + flow edges, one representative per
 * cycle group (closed walks of node indices, first node repeated last); empty
 * when the graph is acyclic. A cheap read for an editor's inline warning: the
 * native runtime runs a flow cycle, but the ratified KHR spec forbids it, so
 * such a graph will not export as KHR_interactivity (the export gate keeps it
 * as Trace behavior data and names the same nodes).
 */
export function findGraphCycles(graph: Pick<GraphData, 'nodes'>): number[][] {
    const result = orderGraphNodes(graph);
    return result.ok ? [] : result.cycles;
}

/** Map authored index → emitted index for an order returned by {@link orderGraphNodes}. */
export function invertOrder(order: readonly number[]): number[] {
    const position = new Array<number>(order.length);
    order.forEach((authored, emitted) => { position[authored] = emitted; });
    return position;
}

function inRange(index: unknown, count: number): index is number {
    return typeof index === 'number' && Number.isInteger(index) && index >= 0 && index < count;
}

function insertSorted(list: number[], value: number): void {
    let i = 0;
    while (i < list.length && list[i] < value) i++;
    list.splice(i, 0, value);
}

/**
 * One representative cycle per strongly-connected component among the nodes
 * Kahn's algorithm could not place (those nodes are exactly the cycles plus
 * whatever hangs downstream of them). Deterministic: lowest index first.
 */
function findCycles(successors: readonly number[][], remaining: ReadonlySet<number>): number[][] {
    const cycles: number[][] = [];
    const covered = new Set<number>();
    for (const start of [...remaining].sort((a, b) => a - b)) {
        if (covered.has(start)) continue;
        const cycle = shortestCycleThrough(start, successors, remaining);
        if (!cycle) continue;
        for (const n of cycle) covered.add(n);
        cycles.push(cycle);
    }
    return cycles;
}

/** BFS for the shortest walk start → … → start inside `allowed`; null if `start` is not on a cycle. */
function shortestCycleThrough(start: number, successors: readonly number[][], allowed: ReadonlySet<number>): number[] | null {
    const parent = new Map<number, number>();
    const queue: number[] = [start];
    const seen = new Set<number>([start]);
    while (queue.length > 0) {
        const current = queue.shift()!;
        for (const next of [...successors[current]].sort((a, b) => a - b)) {
            if (!allowed.has(next)) continue;
            if (next === start) {
                const path = [current];
                let walk = current;
                while (walk !== start) {
                    walk = parent.get(walk)!;
                    path.push(walk);
                }
                path.reverse();
                path.push(start);
                return path;
            }
            if (seen.has(next)) continue;
            seen.add(next);
            parent.set(next, current);
            queue.push(next);
        }
    }
    return null;
}
